import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import Tabs from '../../../components/tabs';
import CampaignInfo from '../drawer/campaignInfo';
import CustomButton from '../../../components/customButton';
import CustomConfirm from '../../../components/customConfirm';
import AddSitesBody from './addSitesBody';
import utils from '../../../utils';
import API from '../../../services/api';
import { useDispatch } from 'react-redux';
import { setAlert } from '../../../redux/dashboard/actions';

const ManageCampaign = ({ campaign, reloadCallback, isOpen, onCancel, toggle }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [campaignData, setCampaignData] = useState(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedTab(0);
    setCampaignData(campaign);
  }, [campaign]);

  useEffect(() => {
    setIsButtonEnabled(willUpdate());
  }, [campaignData, selectedTab]);

  const pages = {
    tabs: [
      {
        name: 'Info',
        content: (
          <div>
            <h2 className="section-header">Info settings</h2>
            <h4 className="section-label">Configure info on {campaign?.displayName}</h4>
            <CampaignInfo campaign={campaign} reloadCallback={reloadCallback} onChange={setCampaignData} />
          </div>
        )
      },
      {
        name: 'Sites',
        content: (
          <div>
            <h2 className="section-header">Sites settings</h2>
            <h4 className="section-label">Manage sites on {campaign?.displayName}</h4>
            <AddSitesBody campaignInfo={campaign} onChangeCB={setCampaignData} />
          </div>
        )
      }
    ]
  };

  if (campaign?.imported) {
    // imported campaigns cannot be edited, so we show only the info tab without the save button
    pages.tabs = pages.tabs.filter(t => t.name === 'Info');
  }

  const willUpdate = () => {
    const timeToMs = t => (t ? new Date(t).getTime() : null);
    const siteValidation = haschangeOnSites();
    switch (selectedTab) {
      case 0:
        if (campaign?.imported) return false; // imported campaigns cannot be edited
        return (
          (campaignData?.displayName || '') !== (campaign?.displayName || '') ||
          timeToMs(campaignData?.startTime) !== timeToMs(campaign?.startTime) ||
          timeToMs(campaignData?.endTime) !== timeToMs(campaign?.endTime)
        );
      case 1:
        return siteValidation;
      default:
        return false;
    }
  };

  const haschangeOnSites = () => {
    const extractIds = arr =>
      (arr || [])
        .map(s => (typeof s === 'string' ? s : s?.id || s?.value || ''))
        .filter(Boolean)
        .map(id => id.toString().trim());

    const a = extractIds(campaign?.sites || []);
    const b = extractIds(campaignData?.selectedSites || campaignData?.sites || []);

    if (a.length !== b.length) return true;

    const setA = new Set(a);
    for (const id of b) {
      if (!setA.has(id)) return true;
    }
    return false;
  };

  const onConfirm = () => {
    if (selectedTab === 0) {
      setLoadingSubmit(true);
      const body = {
        ...campaign,
        displayName: campaignData?.displayName,
        startTime: new Date(campaignData?.startTime).toISOString(),
        endTime: campaignData?.isOngoingCampaign
          ? new Date(9999, 11, 31).toISOString()
          : new Date(campaignData?.endTime).toISOString()
      };

      API.updateCampaign(campaign.companyId, campaign.id, body, ['displayName', 'startTime', 'endTime'])
        .then(() => {
          dispatch(setAlert(utils.generateAlert('Campaign updated successfully!', 'success')));
          reloadCallback();
        })
        .catch(error => {
          dispatch(setAlert(utils.generateAlert(`Error updating campaign: ${error.message}`, 'danger')));
        })
        .finally(() => {
          setLoadingSubmit(false);
          reloadCallback();
        });
    } else if (selectedTab === 1) {
      setLoadingSubmit(true);
      const siteDiffs = updateSites();
      const toAddIds = siteDiffs.toAdd.map(s => s.id);
      const toRemoveIds = siteDiffs.toRemove.map(s => s.id);

      const promises = [];
      if (toAddIds.length) {
        promises.push(API.assignSitesToCampaign(campaign.companyId, campaign.id, toAddIds));
      }
      if (toRemoveIds.length) {
        promises.push(API.unassignSitesFromCampaign(campaign.companyId, campaign.id, toRemoveIds));
      }

      if (promises.length === 0) {
        setLoadingSubmit(false);
        return;
      }

      Promise.all(promises)
        .then(() => {
          dispatch(setAlert(utils.generateAlert('Sites updated successfully!', 'success')));
          reloadCallback();
        })
        .catch(error => {
          dispatch(setAlert(utils.generateAlert(`Error updating sites: ${error.message}`, 'danger')));
        })
        .finally(() => {
          setLoadingSubmit(false);
        });
    }
  };

  const updateSites = () => {
    const normalize = arr =>
      (arr || [])
        .map(s => {
          if (!s) return null;
          if (typeof s === 'string') return { id: s.toString().trim(), label: s };
          const id = s.id || s.value || s.siteId || (s.site && s.site.id) || '';
          const label = s.displayName || s.label || s.name || '';
          return id ? { id: id.toString().trim(), label, raw: s } : null;
        })
        .filter(Boolean);

    const original = normalize(campaign?.sites || []);
    const selected = normalize(campaignData?.selectedSites || campaignData?.sites || []);

    const originalMap = new Map(original.map(s => [s.id, s]));
    const selectedMap = new Map(selected.map(s => [s.id, s]));

    const toAdd = selected.filter(s => !originalMap.has(s.id));
    const toRemove = original.filter(s => !selectedMap.has(s.id));

    return { toAdd, toRemove };
  };

  const handleDelete = () => {
    setLoadingSubmit(true);
    API.deleteCampaign(campaign.companyId, campaign.id)
      .then(() => {
        dispatch(setAlert(utils.generateAlert('Campaign deleted successfully!', 'success')));
      })
      .catch(error => {
        dispatch(setAlert(utils.generateAlert(`Error deleting campaign: ${error.message}`, 'danger')));
      })
      .finally(() => {
        setLoadingSubmit(false);
        setShowDeleteDialog(false);
        reloadCallback();
      });
  };

  return (
    <Modal size="lg" isOpen={isOpen} fade={false} centered id="manage-campaigns-modal">
      <ModalBody>
        <Tabs classes="manage-campaings-tabs" onTabsChange={setSelectedTab} selectedTab={selectedTab} pages={pages} />
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-secondary btn-danger"
          onClick={() => setShowDeleteDialog(true)}
          disabled={campaign?.imported}
        >
          <i className="uil uil-trash" />
          Delete
        </button>

        <CustomButton
          handleClick={onCancel ? onCancel : toggle}
          classes="btn-secondary"
          title="Cancel"
          disabled={false}
        />
        <CustomButton
          handleClick={() => {
            onConfirm();
          }}
          classes="btn-primary"
          title="Confirm changes"
          loading={loadingSubmit}
          disabled={!isButtonEnabled}
        />
        <CustomConfirm
          messageType="deleteCampaign"
          messageContext={campaign}
          confirmCb={() => handleDelete()}
          cancelCb={() => setShowDeleteDialog(false)}
          isOpen={showDeleteDialog}
        />
      </ModalFooter>
    </Modal>
  );
};

export default ManageCampaign;
